// Tela de erro das rotas (QA D-02) — o "error boundary" de cada tela, no painel e no app do aluno: é o
// `errorElement` de toda rota lazy (rotas.tsx), então aparece DENTRO do layout da área (menu/tab bar ficam) e
// nunca a página padrão do React Router nem tela branca.
//
// - Falha ao baixar um chunk lazy (deploy novo com o hash antigo em cache, rede instável): recarrega a
//   página uma vez sozinho, mas só depois de a API responder (/api/saude) — recarregar com o servidor fora
//   dava a página vazia do proxy. Se voltar a falhar, mostra "Recarregar".
// - 429 (LIMITE_EXCEDIDO): texto de "muitas tentativas" (QA D-07).
// - Qualquer outro erro: "Algo deu errado nesta tela" com "Tentar de novo", que refaz a tela sem recarregar a
//   página (zera as consultas com erro e renavega para o mesmo endereço), e "Voltar ao início".
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useRouteError } from "react-router";
import { ehErroApi, MENSAGEM_LIMITE, montarUrl } from "./api/cliente";
import { CHAVE_SESSAO } from "./api/sessao";
import { Botao, Icones } from "./compartilhado/ui";
import { cn } from "./compartilhado/lib/cn";

const CHAVE_RECARGA = "gs:recarga-chunk";
/** Janela em que uma segunda falha de chunk NÃO recarrega de novo (evita laço de recargas). */
const JANELA_RECARGA_MS = 30_000;

/** O erro é de carregamento de módulo/estilo dinâmico (Chrome, Firefox, Safari e o preload do Vite)? */
export function ehErroDeCarregamento(erro: unknown): boolean {
  if (!(erro instanceof Error)) return false;
  return (
    erro.name === "ChunkLoadError" ||
    /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|Loading (CSS )?chunk .* failed/i.test(
      erro.message,
    )
  );
}

/** Pode recarregar agora? (uma vez por janela; false se já recarregou há pouco — aí mostra a tela). */
function podeRecarregar(): boolean {
  try {
    const ultima = Number(sessionStorage.getItem(CHAVE_RECARGA) ?? 0);
    if (Date.now() - ultima < JANELA_RECARGA_MS) return false;
    sessionStorage.setItem(CHAVE_RECARGA, String(Date.now()));
    return true;
  } catch {
    return false; // sem sessionStorage (modo privado antigo): não arrisca laço
  }
}

/** Espera a API responder (deploy em andamento), até ~1 min; devolve se respondeu. */
export async function esperarServidor(sinal: AbortSignal, limiteMs = 60_000): Promise<boolean> {
  const inicio = Date.now();
  for (let espera = 500; Date.now() - inicio < limiteMs; espera = Math.min(espera * 2, 4_000)) {
    try {
      const r = await fetch(montarUrl("/api/saude"), { cache: "no-store", signal: sinal });
      if (r.ok) return true;
    } catch {
      if (sinal.aborted) return false;
    }
    await new Promise((ok) => setTimeout(ok, espera));
    if (sinal.aborted) return false;
  }
  return false;
}

export function ErroRota() {
  const erro = useRouteError();
  const { pathname, search } = useLocation();
  const navegar = useNavigate();
  const queryClient = useQueryClient();
  const painel = pathname.startsWith("/painel");
  const carregamento = ehErroDeCarregamento(erro);
  const limite = ehErroApi(erro, "LIMITE_EXCEDIDO");
  const [recarregando, setRecarregando] = useState(carregamento);

  useEffect(() => {
    if (!carregamento) {
      console.error(erro);
      return;
    }
    if (!podeRecarregar()) {
      setRecarregando(false);
      return;
    }
    const controle = new AbortController();
    void esperarServidor(controle.signal).then((ok) => {
      if (ok) window.location.reload();
      else if (!controle.signal.aborted) setRecarregando(false);
    });
    return () => controle.abort();
  }, [carregamento, erro]);

  /**
   * Refaz a tela sem recarregar a página: descarta o cache (menos a sessão) — o dado que quebrou o render pode
   * ter vindo "com sucesso" — e renavega para o mesmo endereço, o que zera o boundary da rota.
   */
  const tentarDeNovo = () => {
    void queryClient.resetQueries({ predicate: (q) => q.queryKey[0] !== CHAVE_SESSAO[0] });
    void navegar(pathname + search, { replace: true });
  };

  // Painel é claro: tira o tema escuro que o aluno põe no <html> (o layout do painel pode não ter montado).
  useEffect(() => {
    if (!painel) return;
    const html = document.documentElement;
    const antes = html.dataset.tema;
    delete html.dataset.tema;
    return () => {
      if (antes) html.dataset.tema = antes;
    };
  }, [painel]);

  const titulo = carregamento
    ? "O app foi atualizado"
    : limite
      ? "Muitas tentativas"
      : "Algo deu errado nesta tela";
  const texto = carregamento
    ? recarregando
      ? "Abrindo a versão nova…"
      : "Precisamos recarregar para abrir esta tela na versão nova."
    : limite
      ? MENSAGEM_LIMITE
      : "Tente de novo. Se continuar, volte ao início e avise a academia.";

  const inicio = painel ? "/painel" : "/";
  return (
    <div
      role="alert"
      data-tema={painel ? undefined : "escuro"}
      data-erro-rota=""
      className={cn(
        "grid min-h-dvh place-items-center bg-parede px-margem pt-safe pb-safe text-tinta",
        painel && "lg:min-h-[70dvh] lg:bg-transparent",
      )}
    >
      <div className={cn("flex w-full flex-col items-center text-center", painel ? "max-w-md" : "max-w-app")}>
        <Icones.erro size={40} aria-hidden className="mb-4 text-erro" />
        <h1 className="text-h2 text-tinta">{titulo}</h1>
        <p className="mt-2 max-w-72 text-apoio text-texto-2">{texto}</p>
        <div
          className={cn(
            "mt-6 flex w-full flex-col gap-3",
            painel && "sm:w-auto sm:flex-row sm:justify-center",
          )}
        >
          {carregamento ? (
            // Chunk: recarregar a página é o que rebaixa o arquivo novo.
            <Botao
              variante="primario"
              larguraTotal={!painel}
              carregando={recarregando}
              textoCarregando="Recarregando…"
              onClick={() => window.location.reload()}
            >
              Recarregar
            </Botao>
          ) : (
            <Botao variante="primario" larguraTotal={!painel} onClick={tentarDeNovo}>
              Tentar de novo
            </Botao>
          )}
          <Botao variante="secundario" larguraTotal={!painel} onClick={() => window.location.assign(inicio)}>
            Voltar ao início
          </Botao>
        </div>
      </div>
    </div>
  );
}
